import { useCallback } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { API_ROUTES, QUERY_KEYS } from '@/config/constants';
import { api } from '@/lib/axios';
import { useAppStore } from '@/stores/app-store';
import { visibleNotifications } from '@/utils/notification-visibility';
import type { AccountNotificationsResponse, ResolvedNotification } from '@/types/notifications';

const fetchNotifications = async (userId: string): Promise<AccountNotificationsResponse> => {
  const { data } = await api.get<AccountNotificationsResponse>(
    API_ROUTES.notifications.list(userId),
  );
  return data;
};

export const getNotificationsQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: QUERY_KEYS.notifications.list(userId),
    queryFn: () => fetchNotifications(userId),
    staleTime: 0,
    refetchOnMount: 'always' as const,
  });

/**
 * The signed-in user's activity feed, already filtered to what their role may see.
 *
 * The role filter lives here rather than at each call site so the bell's unread badge and the
 * Recent activity table can never disagree, and so a future consumer gets it for free. The cache
 * keeps the unfiltered response — only what this hook hands back is narrowed — so a role or
 * workspace change re-derives from the same data without a refetch.
 */
export const useNotifications = (userId: string | null) => {
  const currentUser = useAppStore((state) => state.currentUser);
  const selectedWholesalerId = useAppStore((state) => state.selectedWholesalerId);

  const select = useCallback(
    (data: AccountNotificationsResponse): AccountNotificationsResponse => ({
      ...data,
      notifications: visibleNotifications(data.notifications, currentUser, selectedWholesalerId),
    }),
    [currentUser, selectedWholesalerId],
  );

  return useQuery({
    ...getNotificationsQueryOptions(userId ?? 'none'),
    enabled: userId != null,
    select,
  });
};

export const countUnread = (notifications: ResolvedNotification[]): number =>
  notifications.filter((n) => n.status === 'unread').length;
